import { useState, useRef, useCallback } from "react";
import QuoteCarousel from "@/components/QuoteCarousel";
import { LogoMark } from "@/components/Logo";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useLocation } from "wouter";
import { UserButton } from "@clerk/react";
import {
  FolderOpen, Upload, Search, Download, Trash2, Archive,
  FileText, ChevronLeft, X, Check, Loader2, AlertTriangle, Eye,
  Package, SortAsc, SortDesc, ArrowUpDown, File, Image,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";

const API_BASE = import.meta.env.BASE_URL.replace(/\/$/, "") + "/api";

const DOCUMENT_TYPES = [
  { value: "regulatory", label: "Regulatory" },
  { value: "licensing", label: "Licensing" },
  { value: "tax", label: "Tax" },
  { value: "correspondence", label: "Correspondence" },
  { value: "payout_record", label: "Payout Record" },
  { value: "audit", label: "Audit" },
  { value: "debt_statement", label: "Debt Statement" },
  { value: "payoff_confirmation", label: "Payoff Confirmation" },
  { value: "tax_certificate", label: "Tax Certificate" },
  { value: "annual_filing", label: "Annual Filing" },
  { value: "other", label: "Other" },
] as const;

type DocType = (typeof DOCUMENT_TYPES)[number]["value"];

interface DocRecord {
  id: number;
  title: string;
  type: DocType;
  category: string | null;
  fileUrl: string;
  mimeType: string | null;
  sizeBytes: number | null;
  uploadedByClerkId: string | null;
  relatedEntityType: string | null;
  relatedEntityId: number | null;
  tags: string[];
  notes: string | null;
  isArchived: boolean;
  createdAt: string;
  updatedAt: string;
  downloadUrl?: string;
}

interface ListResponse {
  data: DocRecord[];
  total: number;
  page: number;
  limit: number;
}

const TYPE_COLORS: Record<DocType, string> = {
  regulatory: "bg-blue-100 text-blue-800",
  licensing: "bg-indigo-100 text-indigo-800",
  tax: "bg-amber-100 text-amber-800",
  correspondence: "bg-slate-100 text-slate-700",
  payout_record: "bg-green-100 text-green-800",
  audit: "bg-purple-100 text-purple-800",
  debt_statement: "bg-orange-100 text-orange-800",
  payoff_confirmation: "bg-teal-100 text-teal-800",
  tax_certificate: "bg-yellow-100 text-yellow-800",
  annual_filing: "bg-cyan-100 text-cyan-800",
  other: "bg-gray-100 text-gray-700",
};

function formatBytes(bytes: number | null) {
  if (bytes === null) return "—";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function formatDate(iso: string) {
  return new Date(iso).toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}

function shortClerkId(id: string | null) {
  if (!id) return "System";
  return id.replace("user_", "").slice(0, 8) + "…";
}

function TypeBadge({ type }: { type: DocType }) {
  const label = DOCUMENT_TYPES.find((t) => t.value === type)?.label ?? type;
  return (
    <span
      className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${TYPE_COLORS[type]}`}
    >
      {label}
    </span>
  );
}

function EntityLink({
  type,
  id,
}: {
  type: string | null;
  id: number | null;
}) {
  if (!type || !id) return <span className="text-muted-foreground">—</span>;
  const label = `${type.replace(/_/g, " ")} #${id}`;
  return (
    <span className="text-xs font-medium text-blue-600 capitalize">{label}</span>
  );
}

function FileIcon({ mimeType }: { mimeType: string | null }) {
  if (!mimeType) return <File className="h-4 w-4 text-muted-foreground" />;
  if (mimeType.includes("pdf")) return <FileText className="h-4 w-4 text-red-500" />;
  if (mimeType.includes("image")) return <Image className="h-4 w-4 text-blue-500" />;
  return <File className="h-4 w-4 text-muted-foreground" />;
}

const EMPTY_UPLOAD = {
  title: "",
  type: "" as DocType | "",
  category: "",
  relatedEntityType: "",
  relatedEntityId: "",
  tags: "",
  notes: "",
};

export default function FilingCabinet() {
  const [, navigate] = useLocation();
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [search, setSearch] = useState("");
  const [typeFilter, setTypeFilter] = useState<string>("all");
  const [categoryFilter, setCategoryFilter] = useState("");
  const [relatedEntityTypeFilter, setRelatedEntityTypeFilter] = useState<string>("all");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [relatedEntityIdFilter, setRelatedEntityIdFilter] = useState("");
  const [showArchived, setShowArchived] = useState(false);
  const [sortBy, setSortBy] = useState("createdAt");
  const [sortDir, setSortDir] = useState<"asc" | "desc">("desc");
  const [page, setPage] = useState(1);

  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
  const [detailDocId, setDetailDocId] = useState<number | null>(null);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [uploadFile, setUploadFileState] = useState<File | null>(null);
  const [uploadForm, setUploadForm] = useState(EMPTY_UPLOAD);
  const [dragOver, setDragOver] = useState(false);

  const [editNotes, setEditNotes] = useState("");
  const [editTags, setEditTags] = useState("");
  const [editCategory, setEditCategory] = useState("");
  const [editMode, setEditMode] = useState(false);

  const [deleteConfirmId, setDeleteConfirmId] = useState<number | null>(null);
  const [exportLoading, setExportLoading] = useState(false);

  const params = new URLSearchParams();
  if (search) params.set("q", search);
  if (typeFilter !== "all") params.set("type", typeFilter);
  if (categoryFilter) params.set("category", categoryFilter);
  if (relatedEntityTypeFilter !== "all") params.set("relatedEntityType", relatedEntityTypeFilter);
  if (dateFrom) params.set("dateFrom", dateFrom);
  if (dateTo) params.set("dateTo", dateTo);
  if (relatedEntityIdFilter) params.set("relatedEntityId", relatedEntityIdFilter);
  if (showArchived) params.set("showArchived", "true");
  params.set("sortBy", sortBy);
  params.set("sortDir", sortDir);
  params.set("page", String(page));
  params.set("limit", "25");

  const { data: listData, isLoading } = useQuery<ListResponse>({
    queryKey: [
      "documents",
      search,
      typeFilter,
      categoryFilter,
      relatedEntityTypeFilter,
      dateFrom,
      dateTo,
      relatedEntityIdFilter,
      showArchived,
      sortBy,
      sortDir,
      page,
    ],
    queryFn: async () => {
      const res = await fetch(`${API_BASE}/admin/documents?${params}`);
      if (!res.ok) throw new Error("Failed to load documents");
      return res.json();
    },
  });

  const { data: detailDoc } = useQuery<DocRecord>({
    queryKey: ["documents", detailDocId],
    enabled: detailDocId !== null,
    queryFn: async () => {
      const res = await fetch(`${API_BASE}/admin/documents/${detailDocId}`);
      if (!res.ok) throw new Error("Failed to load document");
      return res.json();
    },
  });

  const uploadMutation = useMutation({
    mutationFn: async () => {
      if (!uploadFile || !uploadForm.type || !uploadForm.title.trim()) {
        throw new Error("Title, type, and file are required");
      }
      const fd = new FormData();
      fd.append("file", uploadFile);
      fd.append("title", uploadForm.title.trim());
      fd.append("type", uploadForm.type);
      fd.append("category", uploadForm.category);
      fd.append("relatedEntityType", uploadForm.relatedEntityType);
      fd.append("relatedEntityId", uploadForm.relatedEntityId);
      fd.append("tags", uploadForm.tags);
      fd.append("notes", uploadForm.notes);
      const res = await fetch(`${API_BASE}/admin/documents`, {
        method: "POST",
        body: fd,
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error((err as { error?: string }).error ?? "Upload failed");
      }
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["documents"] });
      setUploadOpen(false);
      setUploadFileState(null);
      setUploadForm(EMPTY_UPLOAD);
      toast({
        title: "Document uploaded",
        description: "It's now in the filing cabinet.",
      });
    },
    onError: (err: Error) => {
      toast({ title: "Upload failed", description: err.message, variant: "destructive" });
    },
  });

  const patchMutation = useMutation({
    mutationFn: async ({
      id,
      payload,
    }: {
      id: number;
      payload: Record<string, unknown>;
    }) => {
      const res = await fetch(`${API_BASE}/admin/documents/${id}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      if (!res.ok) throw new Error("Update failed");
      return res.json() as Promise<DocRecord>;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["documents"] });
      setEditMode(false);
      toast({ title: "Document updated" });
    },
    onError: () => {
      toast({ title: "Update failed", variant: "destructive" });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: number) => {
      const res = await fetch(`${API_BASE}/admin/documents/${id}?confirm=true`, {
        method: "DELETE",
      });
      if (!res.ok) throw new Error("Delete failed");
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["documents"] });
      setDeleteConfirmId(null);
      setDetailDocId(null);
      toast({ title: "Document deleted permanently." });
    },
    onError: () => {
      toast({ title: "Delete failed", variant: "destructive" });
    },
  });

  const handleFileSelect = useCallback(
    (file: File) => {
      if (file.size > 25 * 1024 * 1024) {
        toast({
          title: "File too large",
          description: "Max size is 25 MB.",
          variant: "destructive",
        });
        return;
      }
      setUploadFileState(file);
      if (!uploadForm.title) {
        setUploadForm((f) => ({
          ...f,
          title: file.name.replace(/\.[^.]+$/, "").replace(/[-_]/g, " "),
        }));
      }
      setUploadOpen(true);
    },
    [uploadForm.title, toast],
  );

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      setDragOver(false);
      const file = e.dataTransfer.files[0];
      if (file) handleFileSelect(file);
    },
    [handleFileSelect],
  );

  function toggleSort(col: string) {
    if (sortBy === col) {
      setSortDir((d) => (d === "desc" ? "asc" : "desc"));
    } else {
      setSortBy(col);
      setSortDir("desc");
    }
    setPage(1);
  }

  function toggleSelect(id: number) {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  function toggleSelectAll() {
    if (!listData) return;
    if (selectedIds.size === listData.data.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(listData.data.map((d) => d.id)));
    }
  }

  async function handleBulkExport() {
    if (selectedIds.size === 0) return;
    setExportLoading(true);
    try {
      // Enqueue the async zip export job
      const res = await fetch(`${API_BASE}/admin/documents/export`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ids: Array.from(selectedIds) }),
      });
      if (!res.ok) throw new Error("Export failed");
      const { jobId } = await res.json() as { jobId: number };

      toast({ title: "Export queued — packaging your files…" });

      // Poll until completed (max 120s, every 2s)
      let downloadUrl: string | null = null;
      for (let attempt = 0; attempt < 60; attempt++) {
        await new Promise((r) => setTimeout(r, 2000));
        const poll = await fetch(`${API_BASE}/admin/documents/export/job/${jobId}`);
        if (!poll.ok) break;
        const job = await poll.json() as { status: string; downloadUrl: string | null; error: string | null };
        if (job.status === "completed" && job.downloadUrl) {
          downloadUrl = job.downloadUrl;
          break;
        }
        if (job.status === "dead" || job.error) {
          throw new Error(job.error ?? "Export job failed");
        }
      }

      if (!downloadUrl) throw new Error("Export timed out — you will receive an email when it is ready");

      // Trigger browser download using the signed URL
      const a = document.createElement("a");
      const date = new Date().toISOString().slice(0, 10);
      a.href = downloadUrl;
      a.download = `filing-cabinet-export-${date}.zip`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setSelectedIds(new Set());
      toast({ title: "Export ready — download started" });
    } catch (err) {
      toast({
        title: "Export failed",
        description: err instanceof Error ? err.message : "An error occurred",
        variant: "destructive",
      });
    } finally {
      setExportLoading(false);
    }
  }

  function openDetail(doc: DocRecord) {
    setDetailDocId(doc.id);
    setEditNotes(doc.notes ?? "");
    setEditTags((doc.tags ?? []).join(", "));
    setEditCategory(doc.category ?? "");
    setEditMode(false);
  }

  function handleSaveEdit() {
    if (!detailDocId) return;
    patchMutation.mutate({
      id: detailDocId,
      payload: {
        notes: editNotes,
        tags: editTags
          .split(",")
          .map((t) => t.trim())
          .filter(Boolean),
        category: editCategory,
      },
    });
  }

  function handleArchive(doc: DocRecord) {
    patchMutation.mutate({
      id: doc.id,
      payload: { isArchived: !doc.isArchived },
    });
  }

  function clearFilters() {
    setSearch("");
    setTypeFilter("all");
    setCategoryFilter("");
    setRelatedEntityTypeFilter("all");
    setDateFrom("");
    setDateTo("");
    setRelatedEntityIdFilter("");
    setShowArchived(false);
    setPage(1);
  }

  const docs = listData?.data ?? [];
  const total = listData?.total ?? 0;
  const totalPages = Math.ceil(total / 25);
  const hasActiveFilters =
    search ||
    typeFilter !== "all" ||
    categoryFilter ||
    relatedEntityTypeFilter !== "all" ||
    dateFrom ||
    dateTo ||
    relatedEntityIdFilter ||
    showArchived;

  const SortIcon = ({ col }: { col: string }) => {
    if (sortBy !== col) return <ArrowUpDown className="h-3 w-3 ml-1 opacity-40" />;
    return sortDir === "desc" ? (
      <SortDesc className="h-3 w-3 ml-1" />
    ) : (
      <SortAsc className="h-3 w-3 ml-1" />
    );
  };

  return (
    <div className="min-h-screen bg-background">
      <header className="sticky top-0 z-50 border-b bg-slate-900 border-slate-700">
        <div className="max-w-7xl mx-auto px-4 py-3 flex items-center gap-3">
          <button
            onClick={() => navigate("/admin")}
            className="flex items-center gap-1.5 text-slate-300 hover:text-white transition-colors text-sm"
          >
            <ChevronLeft className="h-4 w-4" />
            Admin
          </button>
          <span className="text-slate-600">/</span>
          <div className="flex items-center gap-2">
            <LogoMark size={26} />
            <FolderOpen className="h-5 w-5 text-amber-400" />
            <h1 className="text-lg font-semibold text-white">Filing Cabinet</h1>
          </div>
          <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold bg-red-600 text-white tracking-wide">ADMIN</span>
          <div className="ml-auto">
            <UserButton />
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 py-6 space-y-5">
        {/* Drop zone */}
        <div
          onDragOver={(e) => {
            e.preventDefault();
            setDragOver(true);
          }}
          onDragLeave={() => setDragOver(false)}
          onDrop={handleDrop}
          className={`border-2 border-dashed rounded-xl p-6 text-center transition-colors cursor-pointer ${
            dragOver
              ? "border-amber-400 bg-amber-50"
              : "border-muted-foreground/20 hover:border-amber-300 hover:bg-amber-50/30"
          }`}
          onClick={() => fileInputRef.current?.click()}
        >
          <Upload className="h-8 w-8 mx-auto mb-2 text-amber-500" />
          <p className="text-sm font-medium">
            Drag &amp; drop a file here, or click to upload
          </p>
          <p className="text-xs text-muted-foreground mt-1">
            PDF, Word, Excel, images — up to 25 MB
          </p>
          <input
            ref={fileInputRef}
            type="file"
            className="hidden"
            accept=".pdf,.doc,.docx,.xls,.xlsx,.png,.jpg,.jpeg,.csv,.txt"
            onChange={(e) => {
              const f = e.target.files?.[0];
              if (f) handleFileSelect(f);
              e.target.value = "";
            }}
          />
        </div>

        {/* Search + filters */}
        <div className="space-y-2">
          <div className="flex flex-wrap gap-2 items-center">
            <div className="relative flex-1 min-w-[200px]">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search title, notes, tags, category…"
                value={search}
                onChange={(e) => {
                  setSearch(e.target.value);
                  setPage(1);
                }}
                className="pl-9"
              />
            </div>

            <Select
              value={typeFilter}
              onValueChange={(v) => {
                setTypeFilter(v);
                setPage(1);
              }}
            >
              <SelectTrigger className="w-44">
                <SelectValue placeholder="All types" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All types</SelectItem>
                {DOCUMENT_TYPES.map((t) => (
                  <SelectItem key={t.value} value={t.value}>
                    {t.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Button
              variant={showArchived ? "secondary" : "outline"}
              size="sm"
              onClick={() => {
                setShowArchived((v) => !v);
                setPage(1);
              }}
            >
              <Archive className="h-4 w-4 mr-1" />
              {showArchived ? "Hiding archived" : "Show archived"}
            </Button>

            {hasActiveFilters && (
              <Button variant="ghost" size="sm" onClick={clearFilters}>
                <X className="h-4 w-4 mr-1" />
                Clear filters
              </Button>
            )}

            {selectedIds.size > 0 && (
              <Button
                variant="outline"
                size="sm"
                onClick={handleBulkExport}
                disabled={exportLoading}
                className="ml-auto"
              >
                {exportLoading ? (
                  <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                ) : (
                  <Package className="h-4 w-4 mr-1" />
                )}
                Export {selectedIds.size} as ZIP
              </Button>
            )}
          </div>

          {/* Secondary filters: category, entity type/id, date range */}
          <div className="flex flex-wrap gap-2 items-center">
            <div className="flex items-center gap-1.5">
              <Label className="text-xs text-muted-foreground whitespace-nowrap">
                Category
              </Label>
              <Input
                placeholder="e.g. IRS, Stripe…"
                value={categoryFilter}
                onChange={(e) => {
                  setCategoryFilter(e.target.value);
                  setPage(1);
                }}
                className="h-8 text-xs w-32"
              />
            </div>
            <Select
              value={relatedEntityTypeFilter}
              onValueChange={(v) => {
                setRelatedEntityTypeFilter(v);
                setPage(1);
              }}
            >
              <SelectTrigger className="h-8 text-xs w-36">
                <SelectValue placeholder="All entities" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All entities</SelectItem>
                <SelectItem value="debt">Debt</SelectItem>
                <SelectItem value="donation">Donation</SelectItem>
                <SelectItem value="payout_batch">Payout Batch</SelectItem>
                <SelectItem value="pool">Pool</SelectItem>
              </SelectContent>
            </Select>
            <div className="flex items-center gap-1.5">
              <Label className="text-xs text-muted-foreground whitespace-nowrap">
                Entity ID
              </Label>
              <Input
                type="number"
                placeholder="e.g. 42"
                value={relatedEntityIdFilter}
                onChange={(e) => {
                  setRelatedEntityIdFilter(e.target.value);
                  setPage(1);
                }}
                className="h-8 text-xs w-24"
              />
            </div>
            <div className="flex items-center gap-1.5">
              <Label className="text-xs text-muted-foreground whitespace-nowrap">
                From
              </Label>
              <Input
                type="date"
                value={dateFrom}
                onChange={(e) => {
                  setDateFrom(e.target.value);
                  setPage(1);
                }}
                className="h-8 text-xs w-36"
              />
            </div>
            <div className="flex items-center gap-1.5">
              <Label className="text-xs text-muted-foreground whitespace-nowrap">
                To
              </Label>
              <Input
                type="date"
                value={dateTo}
                onChange={(e) => {
                  setDateTo(e.target.value);
                  setPage(1);
                }}
                className="h-8 text-xs w-36"
              />
            </div>
          </div>
        </div>

        {/* Document list */}
        <Card>
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <CardTitle className="text-base">
                {total > 0
                  ? `${total} document${total !== 1 ? "s" : ""}`
                  : "No documents"}
              </CardTitle>
              {totalPages > 1 && (
                <div className="flex items-center gap-1 text-sm text-muted-foreground">
                  <button
                    disabled={page === 1}
                    onClick={() => setPage((p) => p - 1)}
                    className="px-2 py-1 rounded hover:bg-muted disabled:opacity-40"
                  >
                    ‹
                  </button>
                  <span>
                    {page} / {totalPages}
                  </span>
                  <button
                    disabled={page === totalPages}
                    onClick={() => setPage((p) => p + 1)}
                    className="px-2 py-1 rounded hover:bg-muted disabled:opacity-40"
                  >
                    ›
                  </button>
                </div>
              )}
            </div>
          </CardHeader>
          <CardContent className="p-0">
            {isLoading ? (
              <div className="flex items-center justify-center py-16 text-muted-foreground">
                <Loader2 className="h-6 w-6 animate-spin mr-2" />
                Loading documents…
              </div>
            ) : docs.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-16 text-muted-foreground gap-2">
                <FolderOpen className="h-10 w-10 opacity-30" />
                <p className="text-sm">
                  {hasActiveFilters
                    ? "No documents match your filters."
                    : "The filing cabinet is empty. Upload a document to get started."}
                </p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b">
                      <th className="px-3 py-2 text-left w-8">
                        <input
                          type="checkbox"
                          checked={
                            docs.length > 0 &&
                            selectedIds.size === docs.length
                          }
                          onChange={toggleSelectAll}
                          className="rounded"
                        />
                      </th>
                      <th className="px-3 py-2 text-left">
                        <button
                          onClick={() => toggleSort("title")}
                          className="flex items-center font-medium hover:text-foreground"
                        >
                          Title
                          <SortIcon col="title" />
                        </button>
                      </th>
                      <th className="px-3 py-2 text-left">
                        <button
                          onClick={() => toggleSort("type")}
                          className="flex items-center font-medium hover:text-foreground"
                        >
                          Type
                          <SortIcon col="type" />
                        </button>
                      </th>
                      <th className="px-3 py-2 text-left hidden md:table-cell font-medium">
                        Category
                      </th>
                      <th className="px-3 py-2 text-left hidden lg:table-cell font-medium">
                        Related To
                      </th>
                      <th className="px-3 py-2 text-left hidden lg:table-cell font-medium">
                        Uploaded By
                      </th>
                      <th className="px-3 py-2 text-left hidden lg:table-cell">
                        <button
                          onClick={() => toggleSort("sizeBytes")}
                          className="flex items-center font-medium hover:text-foreground"
                        >
                          Size
                          <SortIcon col="sizeBytes" />
                        </button>
                      </th>
                      <th className="px-3 py-2 text-left hidden xl:table-cell">
                        <button
                          onClick={() => toggleSort("createdAt")}
                          className="flex items-center font-medium hover:text-foreground"
                        >
                          Uploaded
                          <SortIcon col="createdAt" />
                        </button>
                      </th>
                      <th className="px-3 py-2 text-left font-medium">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {docs.map((doc) => (
                      <tr
                        key={doc.id}
                        className={`transition-colors ${
                          doc.isArchived ? "opacity-50" : ""
                        }`}
                      >
                        <td className="px-3 py-2.5">
                          <input
                            type="checkbox"
                            checked={selectedIds.has(doc.id)}
                            onChange={() => toggleSelect(doc.id)}
                            onClick={(e) => e.stopPropagation()}
                            className="rounded"
                          />
                        </td>
                        <td className="px-3 py-2.5">
                          <button
                            onClick={() => openDetail(doc)}
                            className="flex items-center gap-2 text-left hover:text-amber-700 font-medium"
                          >
                            <FileIcon mimeType={doc.mimeType} />
                            <span className="truncate max-w-[160px]">
                              {doc.title}
                            </span>
                            {doc.isArchived && (
                              <span className="text-xs text-muted-foreground">
                                (archived)
                              </span>
                            )}
                          </button>
                          {(doc.tags ?? []).length > 0 && (
                            <div className="flex flex-wrap gap-1 mt-0.5 ml-6">
                              {doc.tags.slice(0, 3).map((tag) => (
                                <span
                                  key={tag}
                                  className="text-[10px] bg-muted px-1.5 py-0.5 rounded-full text-muted-foreground"
                                >
                                  {tag}
                                </span>
                              ))}
                            </div>
                          )}
                        </td>
                        <td className="px-3 py-2.5">
                          <TypeBadge type={doc.type} />
                        </td>
                        <td className="px-3 py-2.5 hidden md:table-cell text-muted-foreground text-xs">
                          {doc.category ?? "—"}
                        </td>
                        <td className="px-3 py-2.5 hidden lg:table-cell">
                          <EntityLink
                            type={doc.relatedEntityType}
                            id={doc.relatedEntityId}
                          />
                        </td>
                        <td className="px-3 py-2.5 hidden lg:table-cell text-muted-foreground text-xs font-mono">
                          {shortClerkId(doc.uploadedByClerkId)}
                        </td>
                        <td className="px-3 py-2.5 hidden lg:table-cell text-muted-foreground text-xs">
                          {formatBytes(doc.sizeBytes)}
                        </td>
                        <td className="px-3 py-2.5 hidden xl:table-cell text-muted-foreground text-xs whitespace-nowrap">
                          {formatDate(doc.createdAt)}
                        </td>
                        <td className="px-3 py-2.5">
                          <div className="flex items-center gap-1">
                            <button
                              onClick={() => openDetail(doc)}
                              title="View details"
                              className="p-1 rounded hover:bg-muted text-muted-foreground hover:text-foreground"
                            >
                              <Eye className="h-4 w-4" />
                            </button>
                            <a
                              href={`${API_BASE}/admin/documents/${doc.id}/download`}
                              title="Download"
                              className="p-1 rounded hover:bg-muted text-muted-foreground hover:text-foreground"
                            >
                              <Download className="h-4 w-4" />
                            </a>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      </main>

      {/* Upload Dialog */}
      <Dialog
        open={uploadOpen}
        onOpenChange={(o) => {
          if (!o) {
            setUploadOpen(false);
            setUploadFileState(null);
            setUploadForm(EMPTY_UPLOAD);
          }
        }}
      >
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Upload className="h-5 w-5 text-amber-600" />
              Upload Document
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4 py-1">
            {uploadFile ? (
              <div className="flex items-center gap-2 p-3 rounded-lg bg-muted text-sm">
                <FileIcon mimeType={uploadFile.type} />
                <span className="truncate flex-1">{uploadFile.name}</span>
                <span className="text-muted-foreground shrink-0">
                  {formatBytes(uploadFile.size)}
                </span>
                <button
                  onClick={() => setUploadFileState(null)}
                  className="text-muted-foreground hover:text-foreground"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
            ) : (
              <div
                className="border-2 border-dashed rounded-lg p-6 text-center cursor-pointer hover:bg-muted/50 transition-colors"
                onClick={() => fileInputRef.current?.click()}
              >
                <Upload className="h-6 w-6 mx-auto mb-1 text-muted-foreground" />
                <p className="text-sm text-muted-foreground">
                  Click to select a file
                </p>
              </div>
            )}

            <div className="space-y-1">
              <Label htmlFor="up-title">Title *</Label>
              <Input
                id="up-title"
                value={uploadForm.title}
                onChange={(e) =>
                  setUploadForm((f) => ({ ...f, title: e.target.value }))
                }
                placeholder="IRS Determination Letter 2025"
              />
            </div>

            <div className="space-y-1">
              <Label htmlFor="up-type">Document Type *</Label>
              <Select
                value={uploadForm.type}
                onValueChange={(v) =>
                  setUploadForm((f) => ({ ...f, type: v as DocType }))
                }
              >
                <SelectTrigger id="up-type">
                  <SelectValue placeholder="Select type…" />
                </SelectTrigger>
                <SelectContent>
                  {DOCUMENT_TYPES.map((t) => (
                    <SelectItem key={t.value} value={t.value}>
                      {t.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label htmlFor="up-category">Category</Label>
                <Input
                  id="up-category"
                  value={uploadForm.category}
                  onChange={(e) =>
                    setUploadForm((f) => ({ ...f, category: e.target.value }))
                  }
                  placeholder="IRS, Stripe, State…"
                />
              </div>
              <div className="space-y-1">
                <Label htmlFor="up-entity-type">Related To</Label>
                <Select
                  value={uploadForm.relatedEntityType || "none"}
                  onValueChange={(v) =>
                    setUploadForm((f) => ({
                      ...f,
                      relatedEntityType: v === "none" ? "" : v,
                    }))
                  }
                >
                  <SelectTrigger id="up-entity-type">
                    <SelectValue placeholder="None" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">None</SelectItem>
                    <SelectItem value="debt">Debt</SelectItem>
                    <SelectItem value="donation">Donation</SelectItem>
                    <SelectItem value="payout_batch">Payout Batch</SelectItem>
                    <SelectItem value="pool">Pool</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {uploadForm.relatedEntityType && uploadForm.relatedEntityType !== "none" && (
              <div className="space-y-1">
                <Label htmlFor="up-entity-id">
                  {uploadForm.relatedEntityType.replace(/_/g, " ")} ID
                </Label>
                <Input
                  id="up-entity-id"
                  type="number"
                  value={uploadForm.relatedEntityId}
                  onChange={(e) =>
                    setUploadForm((f) => ({
                      ...f,
                      relatedEntityId: e.target.value,
                    }))
                  }
                  placeholder="123"
                />
              </div>
            )}

            <div className="space-y-1">
              <Label htmlFor="up-tags">Tags (comma-separated)</Label>
              <Input
                id="up-tags"
                value={uploadForm.tags}
                onChange={(e) =>
                  setUploadForm((f) => ({ ...f, tags: e.target.value }))
                }
                placeholder="501c3, 2025, annual"
              />
            </div>

            <div className="space-y-1">
              <Label htmlFor="up-notes">Notes</Label>
              <Textarea
                id="up-notes"
                value={uploadForm.notes}
                onChange={(e) =>
                  setUploadForm((f) => ({ ...f, notes: e.target.value }))
                }
                placeholder="Any context or notes about this document…"
                rows={2}
              />
            </div>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setUploadOpen(false);
                setUploadFileState(null);
                setUploadForm(EMPTY_UPLOAD);
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={() => uploadMutation.mutate()}
              disabled={
                uploadMutation.isPending ||
                !uploadFile ||
                !uploadForm.title.trim() ||
                !uploadForm.type
              }
            >
              {uploadMutation.isPending ? (
                <>
                  <Loader2 className="h-4 w-4 mr-1 animate-spin" /> Uploading…
                </>
              ) : (
                <>
                  <Upload className="h-4 w-4 mr-1" /> Upload
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Detail Panel */}
      {detailDocId !== null && (
        <Dialog
          open
          onOpenChange={() => {
            setDetailDocId(null);
            setEditMode(false);
          }}
        >
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            {!detailDoc ? (
              <div className="flex items-center justify-center py-16">
                <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
              </div>
            ) : (
              <>
                <DialogHeader>
                  <DialogTitle className="flex items-center gap-2">
                    <FileIcon mimeType={detailDoc.mimeType} />
                    <span className="truncate">{detailDoc.title}</span>
                    {detailDoc.isArchived && (
                      <span className="text-xs font-normal text-muted-foreground">
                        (archived)
                      </span>
                    )}
                  </DialogTitle>
                </DialogHeader>

                <div className="space-y-4">
                  {/* Inline preview using signed download URL */}
                  {detailDoc.downloadUrl && detailDoc.mimeType?.includes("pdf") && (
                    <div
                      className="border rounded-lg overflow-hidden bg-muted"
                      style={{ height: 320 }}
                    >
                      <iframe
                        src={detailDoc.downloadUrl}
                        title="PDF Preview"
                        className="w-full h-full"
                      />
                    </div>
                  )}
                  {detailDoc.downloadUrl && detailDoc.mimeType?.startsWith("image/") && (
                    <div className="border rounded-lg overflow-hidden bg-muted flex items-center justify-center max-h-72">
                      <img
                        src={detailDoc.downloadUrl}
                        alt={detailDoc.title}
                        className="max-h-72 object-contain"
                      />
                    </div>
                  )}

                  {/* Metadata grid */}
                  <div className="grid grid-cols-2 gap-x-6 gap-y-3 text-sm">
                    <div>
                      <p className="text-xs text-muted-foreground mb-0.5">Type</p>
                      <TypeBadge type={detailDoc.type} />
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground mb-0.5">
                        Uploaded By
                      </p>
                      <p className="font-mono text-xs">
                        {shortClerkId(detailDoc.uploadedByClerkId)}
                      </p>
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground mb-0.5">Size</p>
                      <p>{formatBytes(detailDoc.sizeBytes)}</p>
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground mb-0.5">
                        Uploaded
                      </p>
                      <p>{formatDate(detailDoc.createdAt)}</p>
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground mb-0.5">
                        MIME Type
                      </p>
                      <p className="font-mono text-xs">
                        {detailDoc.mimeType ?? "—"}
                      </p>
                    </div>
                    {detailDoc.relatedEntityType && (
                      <div>
                        <p className="text-xs text-muted-foreground mb-0.5">
                          Related Entity
                        </p>
                        <EntityLink
                          type={detailDoc.relatedEntityType}
                          id={detailDoc.relatedEntityId}
                        />
                      </div>
                    )}
                    {detailDoc.category && (
                      <div className="col-span-2">
                        <p className="text-xs text-muted-foreground mb-0.5">
                          Category
                        </p>
                        <p>{detailDoc.category}</p>
                      </div>
                    )}
                  </div>

                  {/* Editable fields */}
                  {editMode ? (
                    <div className="space-y-3 border rounded-lg p-3 bg-muted/30">
                      <div className="space-y-1">
                        <Label className="text-xs">Category</Label>
                        <Input
                          value={editCategory}
                          onChange={(e) => setEditCategory(e.target.value)}
                          placeholder="IRS, Stripe, State…"
                        />
                      </div>
                      <div className="space-y-1">
                        <Label className="text-xs">Tags (comma-separated)</Label>
                        <Input
                          value={editTags}
                          onChange={(e) => setEditTags(e.target.value)}
                          placeholder="501c3, 2025"
                        />
                      </div>
                      <div className="space-y-1">
                        <Label className="text-xs">Notes</Label>
                        <Textarea
                          value={editNotes}
                          onChange={(e) => setEditNotes(e.target.value)}
                          rows={3}
                        />
                      </div>
                      <div className="flex gap-2 justify-end">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setEditMode(false)}
                        >
                          Cancel
                        </Button>
                        <Button
                          size="sm"
                          onClick={handleSaveEdit}
                          disabled={patchMutation.isPending}
                        >
                          {patchMutation.isPending ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                          ) : (
                            <Check className="h-4 w-4 mr-1" />
                          )}
                          Save
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <>
                      {(detailDoc.tags ?? []).length > 0 && (
                        <div>
                          <p className="text-xs text-muted-foreground mb-0.5">
                            Tags
                          </p>
                          <div className="flex flex-wrap gap-1">
                            {detailDoc.tags.map((tag) => (
                              <span
                                key={tag}
                                className="text-xs bg-muted px-2 py-0.5 rounded-full text-muted-foreground"
                              >
                                {tag}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}
                      {detailDoc.notes && (
                        <div>
                          <p className="text-xs text-muted-foreground mb-0.5">
                            Notes
                          </p>
                          <p className="text-sm whitespace-pre-wrap">
                            {detailDoc.notes}
                          </p>
                        </div>
                      )}
                    </>
                  )}
                </div>

                <DialogFooter className="flex-wrap gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setEditMode((e) => !e)}
                  >
                    {editMode ? "Cancel Edit" : "Edit Notes/Tags"}
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleArchive(detailDoc)}
                    disabled={patchMutation.isPending}
                  >
                    <Archive className="h-4 w-4 mr-1" />
                    {detailDoc.isArchived ? "Unarchive" : "Archive"}
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    className="text-destructive hover:text-destructive"
                    onClick={() => setDeleteConfirmId(detailDoc.id)}
                  >
                    <Trash2 className="h-4 w-4 mr-1" />
                    Delete
                  </Button>
                  <a
                    href={`${API_BASE}/admin/documents/${detailDoc.id}/download`}
                    className="inline-flex items-center gap-1 px-3 py-1.5 rounded-md bg-primary text-primary-foreground text-sm font-medium hover:opacity-90 transition-opacity"
                  >
                    <Download className="h-4 w-4" />
                    Download
                  </a>
                </DialogFooter>
              </>
            )}
          </DialogContent>
        </Dialog>
      )}

      {/* Delete Confirm */}
      <Dialog
        open={deleteConfirmId !== null}
        onOpenChange={() => setDeleteConfirmId(null)}
      >
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-destructive">
              <AlertTriangle className="h-5 w-5" />
              Delete Document?
            </DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground">
            This will permanently delete the document and its file from storage.
            This action cannot be undone.
          </p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteConfirmId(null)}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={() =>
                deleteConfirmId !== null &&
                deleteMutation.mutate(deleteConfirmId)
              }
              disabled={deleteMutation.isPending}
            >
              {deleteMutation.isPending ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                "Yes, delete permanently"
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <QuoteCarousel />

    </div>
  );
}
